import './CreateInvoice.sass';

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute } from 'react-router5';

import {
  Button,
  Form,
  FormItem,
  Header,
  Input,
  ScrollView,
  SegmentedControl,
  Select,
} from '$uikit';
import { IcCloud28, IcGallery28, IcVideo28 } from '$assets';
import { AttachPickerManagerRef } from '$shared/components/AttachPickerManager/AttachPickerManager.interface';
import AttachPickerManager from '$shared/components/AttachPickerManager/AttachPickerManager';
import { formatInputAmount } from '$utils/number';
import { toastActions } from '$store/toast';
import { imActions, imFormSelector, imSelector } from '$store/im';
import { useCurrentLang, useCurrentUserInfo, useTranslator, useUserInfo } from '$hooks';
import BigNumber from 'bignumber.js';
import { RootState } from '$store/rootReducer';
import { CLOUD } from '$shared/constants/pages';
import { cloudActions, cloudSelector } from '$store/cloud';
import { mainSelector } from '$store/main';
import { attachPhotosSelector } from '$store/attach';
import { ATTACHMENTS_COUNT_LIMIT, formatCurrency } from '$utils';
import { CurrencyEnum } from '$store/models';
import { LANGUAGE_MAP } from '$shared/constants/languages';
import { goTo } from '$navigation/helper';
import { bundlesActions } from '$store/bundles';

export const CreateInvoice: FC = () => {
  const { route } = useRoute();
  const dispatch = useDispatch();
  const { key: langKey } = useCurrentLang();
  const telegramPost = route.params.type === 'tg';
  const peerId = telegramPost ? 'tg' : route.params.peerId;
  const pickerId = telegramPost ? 'tg_invoice' : `invoice_${peerId}`;
  const {
    message,
    price,
    priceWithoutDiscount,
    lang,
    currency: currencySelected,
  } = useSelector((state: RootState) => imFormSelector(state, peerId));

  const mediaAttachments = useSelector((state: RootState) =>
    attachPhotosSelector(state, pickerId),
  );

  const curUser = useCurrentUserInfo();
  const { tonRate, avgInvoicePrice } = useSelector(imSelector);
  const { mVerified, feePercent, isBlackFriday2025Enabled } = useSelector(mainSelector);
  const { savedContentActions } = useSelector(cloudSelector);
  const t = useTranslator();
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [videoInputKey, setVideoInputKey] = useState(0);
  const attachManager = useRef<AttachPickerManagerRef | null>(null);
  const currency = curUser.verified > 0 ? CurrencyEnum.EUR : CurrencyEnum.TON;
  const peer = useUserInfo(peerId);
  const mediaCount = mediaAttachments.length;
  const attachmentsLimit = ATTACHMENTS_COUNT_LIMIT;

  const currencyRes = currencySelected || currency;

  useEffect(() => {
    if (savedContentActions.length >= 1) {
      savedContentActions.forEach((action) => dispatch(action));
      dispatch(cloudActions.reset());
    }
  }, [savedContentActions]);

  useEffect(() => {
    dispatch(imActions.loadTonRate());
    mVerified &&
      peerId &&
      !telegramPost &&
      dispatch(imActions.loadAvgInvoicePrice(peerId));

    !lang &&
      dispatch(
        imActions.updateFormLang({
          formId: peerId,
          lang: langKey,
        }),
      );

    return () => {
      dispatch(imActions.clearForm(peerId));
      dispatch(imActions.clearAdditionalInfo());
    };
  }, []);

  const handleOnClickCheckAttachmentsLimit = (e) => {
    if (mediaCount >= attachmentsLimit) {
      dispatch(
        toastActions.setToastFail(t('create_invoice_limit_error', { attachmentsLimit })),
      );
      return e.preventDefault();
    }
  };

  const handlePhotoChange = useCallback(
    ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!files) return;
      attachManager.current?.uploadImages(files);
      setPhotoInputKey(Date.now());
    },
    [attachManager, setPhotoInputKey],
  );

  const handleVideoChange = useCallback(
    ({ target }) => {
      if (!target.files.length) {
        return;
      }

      dispatch(imActions.setIsVideoUploading(true));
      attachManager.current?.uploadVideo(target.files[0]);
      setVideoInputKey(Date.now());
    },
    [attachManager],
  );

  const handlePriceChange = ({ target }) => {
    const raw = formatInputAmount(
      target.value,
      currencyRes === CurrencyEnum.PLACE ? 0 : 2,
    );

    const priceV = parseFloat(raw || '0');
    const priceWithoutDiscountV = parseFloat(priceWithoutDiscount || '0');

    dispatch(
      imActions.updateFormPrice({
        formId: peerId,
        price: raw,
      }),
    );
    if (priceV > priceWithoutDiscountV) {
      dispatch(
        imActions.updateFormPriceWithoutDiscount({
          formId: peerId,
          priceWithoutDiscount: raw,
        }),
      );
    }
  };

  const handlePriceWithoutDiscountChange = ({ target }) => {
    dispatch(
      imActions.updateFormPriceWithoutDiscount({
        formId: peerId,
        priceWithoutDiscount: formatInputAmount(
          target.value,
          currencyRes === CurrencyEnum.PLACE ? 0 : 2,
        ),
      }),
    );
  };

  const handleLangChange = ({ target }) => {
    dispatch(
      imActions.updateFormLang({
        formId: peerId,
        lang: target.value,
      }),
    );
  };

  const handleCurrencyChange = (newValue: string) => {
    dispatch(
      imActions.updateFormCurrency({
        formId: peerId,
        currency: newValue,
      }),
    );
  };

  const handleMessageChange = ({ target }) => {
    dispatch(imActions.updateFormMessage({ formId: peerId, message: target.value }));
  };

  const handleSend = () => {
    if (!mediaAttachments.length) {
      dispatch(toastActions.setToastFail(t('create_invoice_attach_empty_error')));
      return;
    }
    if (+price < 1 || !price) {
      dispatch(
        toastActions.setToastFail(
          t('create_invoice_amount_to_small', {
            amount: formatCurrency(1, currency, {
              symbol: false,
            }),
          }),
        ),
      );
      return;
    }

    if (telegramPost) {
      dispatch(imActions.setChooseChatTelegramSheetVisible(true));
      return;
    }

    dispatch(
      imActions.createInvoice({
        peerId,
        price: +price,
        priceWithoutDiscount: +priceWithoutDiscount,
        text: (message || '').trim(),
        currency: currencyRes,
      }),
    );
  };

  const priceTip = useMemo(() => {
    let res;

    const avgPriceTip =
      mVerified && avgInvoicePrice && currencyRes === CurrencyEnum.EUR ? (
        <>
          {t('create_invoice_average_price_description')}{' '}
          <b>
            {formatCurrency(avgInvoicePrice, currencyRes, {
              symbol: CurrencyEnum.PLACE !== currencyRes,
              maximumFractionDigits: 2,
            })}
          </b>
        </>
      ) : (
        ''
      );

    if (+price < 1 || !price) {
      res = t('create_invoice_price_description', {
        amount: formatCurrency(1, currencyRes, {
          symbol: false,
        }),
      });
    } else {
      let fee = feePercent;
      if (peer && peer.isTelegramUser) {
        fee = 0.2;
      } else if (currencyRes === CurrencyEnum.PLACE) {
        fee = 0.1;
      }

      const resultPrice = new BigNumber(price).multipliedBy(1 - fee).toPrecision();
      res = t('create_invoice_price_fee_tip', {
        amount: formatCurrency(+resultPrice, currencyRes, {
          symbol: CurrencyEnum.PLACE !== currencyRes,
          precise: CurrencyEnum.PLACE === currencyRes ? 0 : 2,
        }),
        fee: fee * 100,
      });

      if (tonRate && tonRate > 0 && currencyRes === CurrencyEnum.TON) {
        res += ` (~$${(tonRate * +resultPrice).toFixed(2)})`;
      }
    }

    return (
      <>
        {res}
        {avgPriceTip ? <>. {avgPriceTip}</> : ''}
      </>
    );
  }, [t, price, tonRate, currencyRes, feePercent, peer, avgInvoicePrice]);

  const priceMaxLimit = currencyRes === CurrencyEnum.PLACE ? 5000000 : 300;
  const priceWithoutDiscountMaxLimit = priceMaxLimit * 2;

  const formPrice = Math.max(parseFloat(price || '0'), 0);
  const formPriceWithoutDiscount = Math.max(
    formPrice,
    parseFloat(priceWithoutDiscount || '0'),
  );
  const savings = formPriceWithoutDiscount - formPrice;

  return (
    <>
      <Header showCloseButton>
        {telegramPost ? t('tg_bot_invoice_title') : t('create_invoice_title')}
      </Header>
      <ScrollView>
        <Form>
          <FormItem
            description={
              currencyRes === CurrencyEnum.PLACE
                ? t('create_invoice_place_warning', {
                    currency: currency.toUpperCase(),
                  })
                : null
            }
          >
            <SegmentedControl
              key={savedContentActions.length}
              options={[
                {
                  label: currency.toUpperCase(),
                  value: currency,
                },
                {
                  label: 'PLACE',
                  value: CurrencyEnum.PLACE,
                },
              ]}
              defaultValue={currencyRes}
              onChange={handleCurrencyChange}
            />
          </FormItem>
          {isBlackFriday2025Enabled ? (
            <>
              <FormItem title={t('create_invoice_price_without_discount_label')}>
                <Input
                  type="price"
                  currency={currencyRes}
                  placeholder={t('create_invoice_price_without_discount_placeholder')}
                  value={priceWithoutDiscount}
                  onChange={handlePriceWithoutDiscountChange}
                  minPrice={0}
                  maxPrice={priceWithoutDiscountMaxLimit}
                />
              </FormItem>
              <FormItem
                title={t('create_invoice_price_with_discount_label')}
                description={t('create_invoice_price_with_discount_desc', {
                  amount: formatCurrency(formPrice * 0.8, CurrencyEnum.EUR),
                  cashback: formatCurrency(
                    Math.min(savings, formPrice * 0.2),
                    CurrencyEnum.EUR,
                  ),
                  recommended: formatCurrency(
                    formPriceWithoutDiscount * 0.7,
                    CurrencyEnum.EUR,
                  ),
                })}
              >
                <Input
                  type="price"
                  currency={currencyRes}
                  placeholder={t('create_invoice_price_with_discount_placeholder')}
                  value={price}
                  onChange={handlePriceChange}
                  minPrice={0}
                  maxPrice={priceMaxLimit}
                />
              </FormItem>
            </>
          ) : (
            <FormItem title={t('create_invoice_price_label')} description={priceTip}>
              <Input
                type="price"
                currency={currencyRes}
                placeholder={t('create_invoice_price_placeholder')}
                value={price}
                onChange={handlePriceChange}
                minPrice={0}
                maxPrice={priceMaxLimit}
              />
            </FormItem>
          )}
          {telegramPost && (
            <FormItem
              title={t('create_invoice_lang_label')}
              description={t('create_invoice_lang_description')}
            >
              <Select onChange={handleLangChange} value={lang}>
                {Object.entries(LANGUAGE_MAP).map(([key, language]) => (
                  <option key={key} value={key}>
                    {language}
                  </option>
                ))}
              </Select>
            </FormItem>
          )}
          <FormItem
            title={t('create_invoice_message_label')}
            description={t('create_invoice_message_input_tip')}
          >
            <Input
              multiline
              placeholder={t('create_invoice_message_placeholder')}
              value={message}
              onChange={handleMessageChange}
            />
          </FormItem>
          <FormItem
            title={t('create_invoice_content_label')}
            description={t('create_invoice_content_description')}
          >
            <div className="CreateInvoice__attach_wrap">
              <AttachPickerManager
                ref={attachManager}
                watermark={!telegramPost}
                pickerId={pickerId}
              />
            </div>
            <div className="CreateInvoice__select_button_wrap">
              {mVerified && !telegramPost && (
                <div className="CreateInvoice__select_button">
                  <Button
                    size="small"
                    mode="secondary"
                    icon={<IcCloud28 />}
                    onClick={() => {
                      if (mediaCount >= attachmentsLimit) {
                        dispatch(
                          toastActions.setToastFail(
                            t('create_invoice_limit_error', { attachmentsLimit }),
                          ),
                        );
                        return;
                      }

                      dispatch(
                        cloudActions.setSavedContentActions([
                          imActions.updateFormPrice({ formId: peerId, price }),
                          imActions.updateFormPriceWithoutDiscount({
                            formId: peerId,
                            priceWithoutDiscount,
                          }),
                          imActions.updateFormMessage({ formId: peerId, message }),
                          imActions.updateFormCurrency({
                            formId: peerId,
                            currency: currencyRes,
                          }),
                          // attachActions.pushPhotos({
                          //   pickerId,
                          //   photos: mediaAttachments,
                          // }),
                        ]),
                      );
                      dispatch(
                        cloudActions.setAttachParams({
                          pickerId,
                          routeName: route.name,
                          routeParams: route.params,
                        }),
                      );
                      goTo(
                        CLOUD,
                        {},
                        {
                          limit: attachmentsLimit - mediaCount,
                        },
                      );
                    }}
                  >
                    {t('new_post_attach_cloud')}
                  </Button>
                </div>
              )}
              <div className="CreateInvoice__select_button">
                <Button size="small" mode="secondary" icon={<IcGallery28 />}>
                  {t('new_post_attach_photo')}
                </Button>
                <input
                  key={photoInputKey}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  onClick={handleOnClickCheckAttachmentsLimit}
                  multiple
                />
              </div>
              <div className="CreateInvoice__select_button">
                <Button size="small" mode="secondary" icon={<IcVideo28 />}>
                  {t('new_post_attach_video')}
                </Button>
                <input
                  key={videoInputKey}
                  type="file"
                  accept="video/mp4,video/quicktime"
                  onChange={handleVideoChange}
                  onClick={handleOnClickCheckAttachmentsLimit}
                />
              </div>
            </div>
          </FormItem>
        </Form>
      </ScrollView>
      <div className="CreateInvoice__button">
        <Button onClick={handleSend}>
          {telegramPost ? t('tg_bot_invoice_choose_chat_button') : t('send_button')}
        </Button>
      </div>
    </>
  );
};
